import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";
import { Loader2 } from "lucide-react";
import { Badge } from "@/components/ui/badge";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { initials } from "@/lib/api";

export const FullPageLoader = () => (
  <div className="min-h-screen flex items-center justify-center bg-background" data-testid="full-page-loader">
    <Loader2 className="h-6 w-6 animate-spin text-primary" />
  </div>
);

export const PageHeader = ({ title, subtitle, actions, testid }) => (
  <div className="flex flex-col gap-3 sm:flex-row sm:items-end sm:justify-between mb-6" data-testid={testid || "page-header"}>
    <div>
      <h1 className="text-xl sm:text-2xl font-semibold tracking-tight">{title}</h1>
      {subtitle && <p className="text-sm text-muted-foreground mt-1">{subtitle}</p>}
    </div>
    {actions && <div className="flex flex-wrap gap-2">{actions}</div>}
  </div>
);

const STATUS_STYLES = {
  pending: "bg-[hsl(var(--warning))] text-[hsl(var(--warning-foreground))]",
  approved: "bg-[hsl(var(--success))] text-white",
  executed: "bg-[hsl(var(--success))] text-white",
  achieved: "bg-[hsl(var(--success))] text-white",
  active: "bg-[hsl(var(--success))] text-white",
  done: "bg-[hsl(var(--success))] text-white",
  rejected: "bg-[hsl(var(--destructive))] text-white",
  failed: "bg-[hsl(var(--destructive))] text-white",
  at_risk: "bg-[hsl(var(--destructive))] text-white",
  onboarding: "bg-[hsl(var(--info))] text-white",
  on_track: "bg-[hsl(var(--info))] text-white",
  in_progress: "bg-[hsl(var(--info))] text-white",
  on_leave: "bg-[hsl(var(--warning))] text-[hsl(var(--warning-foreground))]",
  todo: "bg-muted text-foreground",
  draft: "bg-muted text-foreground",
  sent: "bg-[hsl(var(--info))] text-white",
  scheduled: "bg-[hsl(var(--info))] text-white",
  paid: "bg-[hsl(var(--success))] text-white",
  published: "bg-[hsl(var(--success))] text-white",
  overdue: "bg-[hsl(var(--destructive))] text-white",
  inactive: "bg-muted text-muted-foreground",
};

export const StatusBadge = ({ status, testid }) => (
  <Badge
    data-testid={testid || `status-badge-${status}`}
    className={`border-0 capitalize font-medium hover:opacity-90 ${STATUS_STYLES[status] || "bg-muted text-foreground"}`}
  >
    {(status || "").replace(/_/g, " ")}
  </Badge>
);

const PRIORITY = { high: "bg-[hsl(var(--destructive))]", medium: "bg-[hsl(var(--warning))]", low: "bg-[hsl(var(--success))]" };
export const PriorityDot = ({ priority }) => (
  <span className="inline-flex items-center gap-1.5 text-xs text-muted-foreground capitalize">
    <span className={`h-2 w-2 rounded-full ${PRIORITY[priority] || "bg-muted"}`} />
    {priority}
  </span>
);

export const PersonAvatar = ({ name, src, className = "h-8 w-8" }) => (
  <Avatar className={className}>
    {src && <AvatarImage src={src} alt={name} />}
    <AvatarFallback className="bg-accent text-accent-foreground text-xs font-semibold">{initials(name)}</AvatarFallback>
  </Avatar>
);

export const EmptyState = ({ icon: Icon, title, text, action, testid }) => (
  <div className="flex flex-col items-center justify-center text-center py-10 px-4" data-testid={testid || "empty-state"}>
    {Icon && (
      <div className="h-11 w-11 rounded-xl bg-muted flex items-center justify-center mb-3">
        <Icon className="h-5 w-5 text-muted-foreground" />
      </div>
    )}
    <p className="text-sm font-medium">{title}</p>
    {text && <p className="text-xs text-muted-foreground mt-1 max-w-xs">{text}</p>}
    {action && <div className="mt-4">{action}</div>}
  </div>
);

export const Markdown = ({ children }) => (
  <div className="prose-ceo">
    <ReactMarkdown remarkPlugins={[remarkGfm]}>{children || ""}</ReactMarkdown>
  </div>
);

export const Panel = ({ title, icon: Icon, action, children, className = "", testid }) => (
  <section className={`bg-card border border-border rounded-xl ${className}`} data-testid={testid}>
    {(title || action) && (
      <div className="flex items-center justify-between gap-2 px-5 pt-4 pb-3">
        <h2 className="text-sm font-semibold tracking-wide flex items-center gap-2">
          {Icon && <Icon className="h-4 w-4 text-muted-foreground" />}
          {title}
        </h2>
        {action}
      </div>
    )}
    <div className="px-5 pb-5">{children}</div>
  </section>
);
